import type { CommitInfo } from "../types.js";

// Constants from Fern's PR #11502
export const FERN_BOT_NAME = "fern-api";
export const FERN_BOT_EMAIL = "115122769+fern-api[bot]@users.noreply.github.com";
export const FERN_BOT_LOGIN = "fern-api[bot]";

// Fern-support commits (via bot account) are excluded from generation detection.
const FERN_SUPPORT_NAMES = ["fern-support", "Fern Support"];

export function isGenerationCommit(commit: CommitInfo): boolean {
    const isFernSupport = FERN_SUPPORT_NAMES.includes(commit.authorName);

    const isBotAuthor =
        !isFernSupport &&
        (commit.authorLogin === FERN_BOT_LOGIN ||
            commit.authorEmail === FERN_BOT_EMAIL ||
            commit.authorName === FERN_BOT_NAME);

    const hasGenerationMarker =
        commit.message.startsWith("[fern-generated]") ||
        commit.message.startsWith("[fern-replay]") ||
        commit.message.startsWith("[fern-autoversion]") ||
        commit.message.includes("Generated by Fern") ||
        commit.message.includes("\u{1F916} Generated with Fern") ||
        // Squash merge of a Fern-generated PR uses the PR title as commit message.
        // The default PR title is "SDK Generation" (from GithubStep's commitMessage default).
        // GitHub appends "(#N)" for the PR number, e.g. "SDK Generation (#70)".
        commit.message.startsWith("SDK Generation") ||
        // Merge commit (GitHub's "Create a merge commit" option) of a fern-bot
        // regen PR. First-parent walking lands on this commit, not on the
        // generation commit on the side branch, so the classifier must treat
        // it as a generation boundary. GitHub's default subject is
        // "Merge pull request #N from <owner>/<branch>"; fern-bot branches are
        // named "<owner>/fern-bot/<...>".
        /^Merge pull request #\d+ from [^/]+\/fern-bot\//.test(commit.message);

    return isBotAuthor || hasGenerationMarker;
}

/**
 * Narrower predicate than `isGenerationCommit`: which commits reset the
 * customer-customization baseline?
 *
 * `isGenerationCommit` is inclusive (it answers "should the detector skip this
 * commit when emitting patches?"), so it accepts lightweight markers like
 * `[fern-autoversion]` — those bump the version without producing a fresh
 * baseline.
 *
 * The derived-anchor walk needs a stricter predicate: only commits that
 * actually represent a full generation state should be treated as the
 * scan boundary. Otherwise customer commits between the last real generation
 * and an autoversion get dropped (they sit *before* the derived anchor).
 *
 * See docs/adr/0001-derived-scan-boundary.md.
 */
export function isGenerationBoundary(commit: CommitInfo): boolean {
    const isFernSupport = FERN_SUPPORT_NAMES.includes(commit.authorName);

    const isBotAuthor =
        !isFernSupport &&
        (commit.authorLogin === FERN_BOT_LOGIN ||
            commit.authorEmail === FERN_BOT_EMAIL ||
            commit.authorName === FERN_BOT_NAME);

    // Autoversion commits are bot-authored AND prefixed with [fern-autoversion]
    // — they bump version numbers without producing a fresh customization
    // baseline. They must not be the scan boundary regardless of authorship.
    const isAutoversion = commit.message.startsWith("[fern-autoversion]");

    // We deliberately do NOT treat the GitHub merge-commit subject for a
    // fern-bot regen PR (`Merge pull request #N from <owner>/fern-bot/...`)
    // as a boundary. The merge commit sits on first-parent, but the customer
    // fix-up commits authored on the regen PR branch are on the second-parent
    // side. If we stopped at the merge, those fix-ups would be invisible.
    // Continuing past the merge to the previous mainline generation lets the
    // detector walk through the side-branch commits and emit them as patches;
    // any commits already tracked in the lockfile (from the prior regen
    // cycle that opened the PR) get deduped by SHA / content_hash.
    const hasBoundaryMarker =
        commit.message.startsWith("[fern-generated]") ||
        commit.message.startsWith("[fern-replay]") ||
        commit.message.includes("Generated by Fern") ||
        commit.message.includes("\u{1F916} Generated with Fern") ||
        commit.message.startsWith("SDK Generation");

    return ((isBotAuthor && !isAutoversion) || hasBoundaryMarker);
}

export function isReplayCommit(commit: CommitInfo): boolean {
    return commit.message.startsWith("[fern-replay]");
}

/** Check if a commit message matches git's standard revert format: Revert "..." */
export function isRevertCommit(message: string): boolean {
    return /^Revert ".+"$/.test(message);
}

/** Extract the reverted commit SHA from a full commit body containing "This reverts commit SHA." */
export function parseRevertedSha(fullBody: string): string | undefined {
    const match = fullBody.match(/This reverts commit ([0-9a-f]{40})\./);
    return match?.[1];
}

/** Extract the original commit message from a revert subject like 'Revert "original message"' */
export function parseRevertedMessage(subject: string): string | undefined {
    const match = subject.match(/^Revert "(.+)"$/);
    return match?.[1];
}
